import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Dialog from 'components/Dialog';
import { useForm, useTranslation } from 'hooks';
import Form from './Form';

function ExecutiveSummary({ executiveSummary, language, syncErrors, syncing, onSave }) {
  const { t, tr } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const { useInput, handleReset, handleSubmit } = useForm({
    initialValues: executiveSummary,
    onSubmit: onSave
  });

  const prevSyncingRef = useRef(syncing);

  const handleClose = useCallback(() => {
    handleReset?.();
    navigate(`/resume/${id ?? ''}`);
  }, [handleReset, navigate, id]);

  useEffect(() => {
    if (prevSyncingRef.current && !syncing && !syncErrors) {
      handleClose();
    }
    prevSyncingRef.current = syncing;
  }, [syncing, syncErrors, handleClose]);

  // Only render if this is a modal route (background state exists)
  const isModal = location.state?.background;
  const isOpen = isModal && location.pathname.endsWith('/executive-summary');

  // Don't render if not a modal
  if (!isModal) return null;

  return (
    <Dialog
      actionLabel={t('button.save')}
      disabled={syncing}
      open={isOpen}
      syncing={syncing}
      title={tr('title.executiveSummary')}
      onAction={handleSubmit}
      onClose={handleClose}
    >
      <Form data-test-id="executive-summary-form" language={language} useInput={useInput} />
    </Dialog>
  );
}

ExecutiveSummary.propTypes = {
  executiveSummary: PropTypes.object.isRequired,
  language: PropTypes.string.isRequired,
  syncErrors: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  syncing: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired
};

export default ExecutiveSummary;
